/*
 * UpcomingMatchHero — Iter 91
 * ====================================================================
 * Split showcase card sitting under the main hero:
 *   ┌─────────────────────────────┬─────────────────────────────┐
 *   │       NEXT GAME              │        LAST GAME            │
 *   │  teams · countdown · predict │  teams · final score        │
 *   └─────────────────────────────┴─────────────────────────────┘
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │  Pos · Matches · Goals · Points  (season stats bar)           │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * All in the Lefteria FC visual language: pitch-black surfaces, gold
 * accents, Bebas Neue display. Countdown, match-type badge and the
 * anonymous score-prediction form are preserved from Iter 90.
 */
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Calendar, MapPin, Clock, Trophy, Zap, Send, CheckCircle2, Flag } from "lucide-react";
import { CLUB_LOGO } from "../club-site/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OUR_TOKENS = ["lefteria", "λευτερια", "λευτέρια"];
const CLUB_NAME_GOLD = "ΛΕΥΤΕΡΙΑ FC";

const isOurTeam = (n) => {
  const s = (n || "").toLowerCase();
  return OUR_TOKENS.some(t => s.includes(t));
};

const displayTeamName = (n) => (isOurTeam(n) ? CLUB_NAME_GOLD : (n || ""));

// Iter 91.4 — Cached club profile logo. Shared across every TeamBlock
// so we can auto-substitute the club logo whenever a fixture row is missing
// the home/away team logo for our own side.
const CLUB_LOGO_FALLBACK = CLUB_LOGO;
let _clubLogoPromise = null;
const getClubLogo = () => {
  if (!_clubLogoPromise) {
    _clubLogoPromise = axios.get(`${API}/club`)
      .then(r => (r.data && r.data.logo_url) || CLUB_LOGO_FALLBACK)
      .catch(() => CLUB_LOGO_FALLBACK);
  }
  return _clubLogoPromise;
};

const resolveImg = (u) => u
  ? (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`)
  : null;

// ============================================================
// Countdown hook
// ============================================================
const useCountdown = (target) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!target) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [target]);
  return useMemo(() => {
    if (!target) return null;
    const diff = new Date(target).getTime() - now;
    if (Number.isNaN(diff)) return null;
    if (diff <= 0) return { done: true, d: 0, h: 0, m: 0, s: 0 };
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return { done: false, d, h, m, s };
  }, [target, now]);
};

const TIME_UNIT = { d: "Ημερες", h: "Ωρες", m: "Λεπτα", s: "Δευτ." };

const MATCH_TYPE = {
  championship: { label: "ΠΡΩΤΑΘΛΗΜΑ", color: "#F5A623" },
  cup:          { label: "ΚΥΠΕΛΛΟ",     color: "#DC2626" },
  friendly:     { label: "ΦΙΛΙΚΟ",      color: "#10B981" },
  tournament:   { label: "ΤΟΥΡΝΟΥΑ",    color: "#8B5CF6" },
};

// ============================================================
// Team block (logo + name) — shared between Next & Last
// ============================================================
// ============================================================
// Team crest — shared circular team logo. Auto-substitutes our
// club logo when the fixture has none for our team, zooms in
// aggressively so branded crests fill the frame with no bleed of
// baked-in image whitespace.
// ============================================================
export const TeamCrest = ({ name, logo, size = "md" }) => {
  const [autoLogo, setAutoLogo] = useState(null);
  useEffect(() => {
    if (logo || !isOurTeam(name)) return;
    let alive = true;
    getClubLogo().then(u => { if (alive) setAutoLogo(u); });
    return () => { alive = false; };
  }, [logo, name]);
  const effectiveLogo = logo || autoLogo;
  const dims = {
    xs: "w-6 h-6",
    sm: "w-8 h-8",
    md: "w-24 h-24 sm:w-28 sm:h-28",
    lg: "w-28 h-28 sm:w-32 sm:h-32",
  }[size] || "w-24 h-24";

  const src = resolveImg(effectiveLogo);
  if (src) {
    return (
      <div className={`${dims} rounded-full overflow-hidden flex-shrink-0 shadow-md ring-1 ring-white/10 bg-black`}>
        <img src={src} alt="" className="w-full h-full object-contain scale-[1.15]" />
      </div>
    );
  }
  // Text fallback keeps the white pill so the initials stay legible.
  return (
    <div className={`${dims} rounded-full bg-white flex items-center justify-center overflow-hidden flex-shrink-0 shadow-md ring-1 ring-white/10`}>
      <span className="font-['Bebas_Neue'] text-zinc-800 text-[10px] sm:text-base leading-none">
        {(name || "?").slice(0, 3).toUpperCase()}
      </span>
    </div>
  );
};

const TeamBlock = ({ name, logo, size = "md" }) => {
  const label = size === "lg"
    ? "font-['Bebas_Neue'] text-xl sm:text-2xl tracking-wide leading-tight"
    : "font-['Bebas_Neue'] text-lg sm:text-xl tracking-wide leading-tight";
  return (
    <div className="flex flex-col items-center text-center gap-2.5 min-w-0">
      <TeamCrest name={name} logo={logo} size={size} />
      <span className={`${label} ${isOurTeam(name) ? "text-[#F5A623]" : "text-white"} max-w-[150px] truncate`}>
        {displayTeamName(name)}
      </span>
    </div>
  );
};

// ============================================================
// Score prediction — submits, then shows the fanbase aggregate
// ============================================================
const ScorePrediction = ({ fixtureId, homeName, awayName }) => {
  const key = `pred_${fixtureId}`;
  const [home, setHome] = useState("");
  const [away, setAway] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [agg, setAgg] = useState(null);

  const loadAgg = async () => {
    try {
      const r = await axios.get(`${API}/predictions/${fixtureId}`);
      setAgg(r.data || null);
    } catch { /* silent */ }
  };

  useEffect(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(key) || "null");
      if (cached) {
        setHome(String(cached.h));
        setAway(String(cached.a));
        setSubmitted(true);
        loadAgg();
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const submit = async (e) => {
    e.preventDefault();
    const h = parseInt(home, 10), a = parseInt(away, 10);
    if (!Number.isFinite(h) || !Number.isFinite(a)) return;
    try {
      await axios.post(`${API}/predictions`, { fixture_id: fixtureId, home_score: h, away_score: a })
        .catch(() => {});
    } catch { /* ignore */ }
    localStorage.setItem(key, JSON.stringify({ h, a }));
    setSubmitted(true);
    loadAgg();
  };

  if (submitted) {
    const myKey = `${home}-${away}`;
    return (
      <div className="space-y-2" data-testid="prediction-submitted">
        <div className="flex items-center justify-center gap-2 text-emerald-400 text-xs">
          <CheckCircle2 size={14} /> Πρόβλεψή σου:
          <span className="font-['Bebas_Neue'] text-lg text-[#F5A623]">{home}-{away}</span>
        </div>
        {agg && agg.count > 0 && (
          <div className="bg-[#0a0a0a]/70 border border-[#1e1e1e] rounded-md px-3 py-2" data-testid="predictions-aggregate">
            <div className="flex items-center justify-between text-[9px] uppercase tracking-widest text-zinc-500 mb-1.5">
              <span>Οι Προβλεψεις των Φιλαθλων</span>
              <span className="text-[#F5A623]">{agg.count} ψηφοι</span>
            </div>
            <div className="space-y-1">
              {agg.top.map((t, i) => (
                <div key={t.score} className="flex items-center gap-2 text-[10px]">
                  <span className={`font-['Bebas_Neue'] text-sm w-10 text-center rounded ${t.score === myKey ? "text-[#F5A623] bg-[#F5A623]/10" : "text-white"}`}>
                    {t.score}
                  </span>
                  <div className="flex-1 h-1.5 bg-[#1a1a1a] rounded-full overflow-hidden">
                    <div
                      className={`h-full ${i === 0 ? "bg-[#F5A623]" : "bg-[#F5A623]/50"}`}
                      style={{ width: `${t.percent}%` }}
                    />
                  </div>
                  <span className="text-zinc-400 w-10 text-right tabular-nums">{t.percent}%</span>
                </div>
              ))}
            </div>
            {agg.average && (
              <div className="mt-2 pt-2 border-t border-[#1a1a1a] text-center text-[9px] text-zinc-500 uppercase tracking-widest">
                Μεσος ορος: <span className="text-white font-['Bebas_Neue'] text-sm">{agg.average.home} - {agg.average.away}</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="flex items-center justify-center gap-1.5 text-[10px] text-zinc-400" data-testid="prediction-form">
      <span className="uppercase tracking-wider">{displayTeamName(homeName).split(" ")[0]}</span>
      <input
        type="text" inputMode="numeric" pattern="[0-9]*" maxLength={2}
        value={home}
        onChange={e => setHome(e.target.value.replace(/[^0-9]/g, ""))}
        className="predict-input w-10 h-8 text-center bg-[#0a0a0a] border border-[#333] rounded font-['Bebas_Neue'] text-white text-base focus:outline-none focus:border-[#F5A623]"
        data-testid="predict-home"
      />
      <span className="text-zinc-600">·</span>
      <input
        type="text" inputMode="numeric" pattern="[0-9]*" maxLength={2}
        value={away}
        onChange={e => setAway(e.target.value.replace(/[^0-9]/g, ""))}
        className="predict-input w-10 h-8 text-center bg-[#0a0a0a] border border-[#333] rounded font-['Bebas_Neue'] text-white text-base focus:outline-none focus:border-[#F5A623]"
        data-testid="predict-away"
      />
      <span className="uppercase tracking-wider">{displayTeamName(awayName).split(" ")[0]}</span>
      <button type="submit" className="ml-1 admin-btn-primary !py-1 !px-2 !text-[10px]" data-testid="predict-submit">
        <Send size={10} /> Στειλε
      </button>
    </form>
  );
};

// ============================================================
// Head-to-Head strip (past results vs the same opponent)
// ============================================================
const HeadToHeadStrip = ({ homeTeam, awayTeam }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!homeTeam || !awayTeam) return;
    // Prefer our team as team_a so the summary reads from our perspective.
    // Pass every known alias for our club so Latin/Greek historic spellings
    // both match (archived seasons may use different casings).
    const our = isOurTeam(homeTeam) ? homeTeam : (isOurTeam(awayTeam) ? awayTeam : homeTeam);
    const opp = our === homeTeam ? awayTeam : homeTeam;
    const ourAliases = isOurTeam(our)
      ? ["Lefteria", "ΛΕΥΤΕΡΙΑ", "Λευτεριά", "Λευτερια"].join(",")
      : our;
    (async () => {
      try {
        const r = await axios.get(`${API}/fixtures/head-to-head`, { params: { team_a: ourAliases, team_b: opp, limit: 5 } });
        setData(r.data || null);
      } catch { /* silent */ }
    })();
  }, [homeTeam, awayTeam]);

  if (!data || !data.summary || data.summary.played === 0) return null;
  const { wins, draws, losses } = data.summary;

  return (
    <div className="mx-4 mb-3 bg-[#0a0a0a]/70 border border-[#1e1e1e] rounded-md px-3 py-2" data-testid="h2h-strip">
      <div className="flex items-center justify-between text-[9px] uppercase tracking-widest text-zinc-500 mb-1.5">
        <span>Ιστορικο Αναμετρησεων</span>
        <span className="text-[#F5A623]">{data.summary.played} αγωνες</span>
      </div>
      {/* Summary pills */}
      <div className="grid grid-cols-3 gap-2 mb-2">
        <div className="text-center bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-1">
          <div className="font-['Bebas_Neue'] text-lg text-emerald-400 leading-none tabular-nums">{wins}</div>
          <div className="text-[8px] uppercase tracking-widest text-emerald-500/80">Νικες</div>
        </div>
        <div className="text-center bg-zinc-500/10 border border-zinc-500/30 rounded px-2 py-1">
          <div className="font-['Bebas_Neue'] text-lg text-zinc-300 leading-none tabular-nums">{draws}</div>
          <div className="text-[8px] uppercase tracking-widest text-zinc-400">Ισοπαλιες</div>
        </div>
        <div className="text-center bg-red-500/10 border border-red-500/30 rounded px-2 py-1">
          <div className="font-['Bebas_Neue'] text-lg text-red-400 leading-none tabular-nums">{losses}</div>
          <div className="text-[8px] uppercase tracking-widest text-red-500/80">Ηττες</div>
        </div>
      </div>
      {/* Last 3 fixtures */}
      <div className="space-y-1">
        {data.fixtures.slice(0, 3).map((f) => {
          const d = (() => {
            try { return new Date(f.match_date).toLocaleDateString("el-GR", { day: "2-digit", month: "2-digit", year: "2-digit" }); }
            catch { return f.match_date; }
          })();
          const wasOurHome = isOurTeam(f.home_team);
          const ourScore = wasOurHome ? f.home_score : f.away_score;
          const oppScore = wasOurHome ? f.away_score : f.home_score;
          let dot = "bg-zinc-400";
          if (ourScore > oppScore) dot = "bg-emerald-400";
          else if (ourScore < oppScore) dot = "bg-red-400";
          return (
            <div key={f.id} className="flex items-center gap-2 text-[10px] text-zinc-300">
              <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
              <span className="text-zinc-500 tabular-nums w-14">{d}</span>
              <span className="flex-1 truncate">
                {displayTeamName(f.home_team)} vs {displayTeamName(f.away_team)}
                {f.archived_season && (
                  <span className="ml-1.5 px-1 py-0.5 rounded bg-[#F5A623]/10 border border-[#F5A623]/30 text-[#F5A623] text-[8px] uppercase tracking-widest align-middle">
                    {f.archived_season}
                  </span>
                )}
              </span>
              <span className="font-['Bebas_Neue'] text-sm text-white tabular-nums">{f.home_score ?? "-"}-{f.away_score ?? "-"}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================
// NEXT GAME panel
// ============================================================
const NextGamePanel = ({ fixture }) => {
  const matchType = MATCH_TYPE[fixture.match_type] || MATCH_TYPE.championship;

  const target = useMemo(() => {
    const d = fixture.match_date, t = fixture.match_time || "18:00";
    if (!d) return null;
    if (d.includes("T")) return d;
    return `${d}T${t}${t.length === 5 ? ":00" : ""}`;
  }, [fixture]);

  const countdown = useCountdown(target);

  const dateStr = (() => {
    try { return new Date(fixture.match_date).toLocaleDateString("el-GR", { day: "numeric", month: "long", year: "numeric" }); }
    catch { return fixture.match_date; }
  })();

  return (
    <div className="flex flex-col" data-testid="next-game-panel">
      {/* Panel header — gold ribbon */}
      <div className="bg-gradient-to-r from-[#F5A623] to-[#e0921a] px-4 py-2 flex items-center justify-between">
        <span className="font-['Bebas_Neue'] text-lg sm:text-xl tracking-widest text-black flex items-center gap-2">
          <Flag size={14} /> NEXT GAME
        </span>
        <span
          className="inline-flex items-center gap-1 text-[9px] px-2 py-0.5 rounded uppercase tracking-widest font-bold bg-black/80 text-white"
          style={{ borderLeft: `2px solid ${matchType.color}` }}
        >
          <Trophy size={9} style={{ color: matchType.color }} /> {matchType.label}
        </span>
      </div>

      {/* Sub header — date */}
      <div className="px-4 py-2.5 border-b border-[#F5A623]/20 bg-[#0a0a0a]/80 flex items-center justify-center gap-3 text-[12px] sm:text-[13px] text-white font-medium">
        <span className="flex items-center gap-1.5"><Calendar size={13} className="text-[#F5A623]" /> {dateStr}</span>
        {fixture.match_time && <span className="flex items-center gap-1.5"><Clock size={13} className="text-[#F5A623]" /> {fixture.match_time}</span>}
      </div>

      {/* Body — teams + VS */}
      <div className="relative flex-1 flex flex-col justify-center px-4 py-5 sm:py-6 min-h-[200px]">
        <div className="absolute inset-0 opacity-30 pointer-events-none"
             style={{ background: "radial-gradient(circle at 30% 40%, #F5A62333, transparent 60%)" }} />
        <div className="relative grid grid-cols-[1fr_auto_1fr] items-center gap-3">
          <TeamBlock name={fixture.home_team} logo={fixture.home_team_logo} />
          <div className="flex flex-col items-center gap-2">
            <span className="font-['Bebas_Neue'] text-2xl sm:text-4xl text-white/90 leading-none">VS</span>
          </div>
          <TeamBlock name={fixture.away_team} logo={fixture.away_team_logo} />
        </div>

        {/* Countdown */}
        {countdown && !countdown.done && (
          <div className="mt-4 flex items-center justify-center gap-1.5" data-testid="countdown">
            {(["d", "h", "m", "s"]).map(u => (
              <div key={u} className="bg-[#0a0a0a] border border-[#F5A623]/30 rounded-md px-2 py-1 min-w-[42px]">
                <div className="font-['Bebas_Neue'] text-base sm:text-lg text-[#F5A623] leading-none tabular-nums text-center">
                  {String(countdown[u]).padStart(2, "0")}
                </div>
                <div className="text-[7px] text-zinc-500 uppercase tracking-wider mt-0.5 text-center">{TIME_UNIT[u]}</div>
              </div>
            ))}
          </div>
        )}
        {countdown && countdown.done && (
          <div className="mt-3 mx-auto inline-flex items-center gap-1 px-3 py-1 bg-red-500/15 border border-red-500/40 rounded-full text-red-400 text-xs uppercase tracking-wider animate-pulse">
            <Zap size={10} /> Live τώρα
          </div>
        )}
      </div>

      {/* Footer — venue + prediction */}
      <div className="px-4 pb-4 space-y-2 border-t border-[#1e1e1e] pt-3">
        {fixture.venue && (
          <div className="flex items-center justify-center gap-1.5 text-[12px] text-white font-medium">
            <MapPin size={13} className="text-[#F5A623]" /> {fixture.venue}
          </div>
        )}
        <p className="text-center text-[9px] text-zinc-500 uppercase tracking-widest">Μαντεψε το σκορ</p>
        <ScorePrediction fixtureId={fixture.id} homeName={fixture.home_team} awayName={fixture.away_team} />
      </div>
      {/* H2H strip */}
      <HeadToHeadStrip homeTeam={fixture.home_team} awayTeam={fixture.away_team} />
    </div>
  );
};

// ============================================================
// LAST GAME panel
// ============================================================
const LastGamePanel = ({ fixture }) => {
  const matchType = MATCH_TYPE[fixture.match_type] || MATCH_TYPE.championship;
  const dateStr = (() => {
    try { return new Date(fixture.match_date).toLocaleDateString("el-GR", { day: "numeric", month: "long", year: "numeric" }); }
    catch { return fixture.match_date; }
  })();

  const home = fixture.home_score;
  const away = fixture.away_score;
  const wasOurHome = isOurTeam(fixture.home_team);
  const wasOurAway = isOurTeam(fixture.away_team);
  let outcome = null;
  if (typeof home === "number" && typeof away === "number") {
    if (home === away) outcome = { label: "ΙΣΟΠΑΛΙΑ", color: "#94a3b8" };
    else if ((home > away && wasOurHome) || (away > home && wasOurAway)) outcome = { label: "ΝΙΚΗ", color: "#10B981" };
    else if (wasOurHome || wasOurAway) outcome = { label: "ΗΤΤΑ", color: "#DC2626" };
  }

  return (
    <div className="flex flex-col" data-testid="last-game-panel">
      {/* Panel header — silver/white ribbon */}
      <div className="bg-gradient-to-r from-[#1a1a1a] via-[#242424] to-[#1a1a1a] px-4 py-2 flex items-center justify-between border-b border-[#F5A623]/30">
        <span className="font-['Bebas_Neue'] text-lg sm:text-xl tracking-widest text-white flex items-center gap-2">
          <Trophy size={14} className="text-[#F5A623]" /> LAST GAME
        </span>
        <span
          className="inline-flex items-center gap-1 text-[9px] px-2 py-0.5 rounded uppercase tracking-widest font-bold text-white/80"
          style={{ background: `${matchType.color}22`, border: `1px solid ${matchType.color}55` }}
        >
          {matchType.label}
        </span>
      </div>

      {/* Sub header — date */}
      <div className="px-4 py-2.5 border-b border-[#1a1a1a] bg-[#0a0a0a]/80 flex items-center justify-center gap-3 text-[12px] sm:text-[13px] text-white font-medium">
        <span className="flex items-center gap-1.5"><Calendar size={13} className="text-[#F5A623]" /> {dateStr}</span>
        {outcome && (
          <span className="uppercase tracking-widest font-bold text-[11px]" style={{ color: outcome.color }}>
            · {outcome.label}
          </span>
        )}
      </div>

      {/* Body — teams + final score */}
      <div className="relative flex-1 flex flex-col justify-center px-4 py-5 sm:py-6 min-h-[200px]">
        <div className="absolute inset-0 opacity-25 pointer-events-none"
             style={{ background: "radial-gradient(circle at 70% 40%, #F5A62333, transparent 60%)" }} />
        <div className="relative grid grid-cols-[1fr_auto_1fr] items-center gap-3">
          <TeamBlock name={fixture.home_team} logo={fixture.home_team_logo} />
          <div className="flex flex-col items-center gap-1">
            <div className="bg-[#0a0a0a] border border-[#F5A623]/50 rounded-lg px-3 sm:px-4 py-1.5 sm:py-2 shadow-[0_0_20px_rgba(245,166,35,0.15)]">
              <span className="font-['Bebas_Neue'] text-2xl sm:text-4xl text-[#F5A623] leading-none tabular-nums">
                {home ?? "-"} <span className="text-white/60">·</span> {away ?? "-"}
              </span>
            </div>
            <span className="text-[9px] text-zinc-500 uppercase tracking-widest">Τελικο</span>
          </div>
          <TeamBlock name={fixture.away_team} logo={fixture.away_team_logo} />
        </div>
      </div>

      {/* Footer — venue */}
      <div className="px-4 pb-4 border-t border-[#1e1e1e] pt-3 min-h-[62px] flex items-center justify-center">
        {fixture.venue ? (
          <div className="flex items-center justify-center gap-1.5 text-[12px] text-white font-medium">
            <MapPin size={13} className="text-[#F5A623]" /> {fixture.venue}
          </div>
        ) : (
          <span className="text-[10px] text-zinc-600 uppercase tracking-widest">Ολοκληρωθηκε</span>
        )}
      </div>
    </div>
  );
};

// ============================================================
// Stats strip (position, matches, goals, points)
// ============================================================
const StatsStrip = ({ stats }) => {
  if (!stats || !stats.length) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-[#1e1e1e] bg-[#0a0a0a]/80 border-t border-[#F5A623]/20" data-testid="season-stats-strip">
      {stats.map((s, i) => (
        <div key={i} className="text-center px-4 py-4 sm:py-5">
          <div className="font-['Bebas_Neue'] text-3xl md:text-4xl text-[#F5A623] leading-none tabular-nums">{s.value}</div>
          <div className="text-[10px] sm:text-xs text-zinc-400 tracking-widest uppercase mt-1">{s.label}</div>
        </div>
      ))}
    </div>
  );
};

// ============================================================
// Main hero — split showcase + stats
// ============================================================
export const UpcomingMatchHero = ({ stats }) => {
  const [next, setNext] = useState(null);
  const [last, setLast] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [upRes, doneRes] = await Promise.all([
          axios.get(`${API}/fixtures?status=Scheduled&limit=1`).catch(() => ({ data: [] })),
          axios.get(`${API}/fixtures?status=Completed&limit=1`).catch(() => ({ data: [] })),
        ]);
        const upNext = (upRes.data || []).find(f => f.match_date && !Number.isNaN(new Date(f.match_date).getTime())) || null;
        const upLast = (doneRes.data || []).find(f => f.match_date && !Number.isNaN(new Date(f.match_date).getTime())) || null;
        setNext(upNext);
        setLast(upLast);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return null;
  if (!next && !last && !(stats && stats.length)) return null;

  return (
    <>
      <section className="relative -mt-[50px] pb-10 z-20 px-4" data-testid="upcoming-match-hero">
        <div className="max-w-6xl mx-auto">
          <div className="relative bg-gradient-to-br from-[#101010] via-[#0a0a0a] to-[#101010] border border-[#F5A623]/40 rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(245,166,35,0.15)]">
            {/* Split panels */}
            {next && last ? (
              <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#F5A623]/20">
                <NextGamePanel fixture={next} />
                <LastGamePanel fixture={last} />
              </div>
            ) : next ? (
              <NextGamePanel fixture={next} />
            ) : last ? (
              <LastGamePanel fixture={last} />
            ) : null}
          </div>
        </div>
      </section>

      {/* Separate season stats section */}
      {stats && stats.length > 0 && (
        <section className="relative pb-10 z-10 px-4" data-testid="season-stats-section">
          <div className="max-w-6xl mx-auto">
            <div className="mb-4 flex items-end justify-between">
              <div>
                <span className="badge badge-secondary mb-2">Στατιστικα</span>
                <h2 className="font-['Bebas_Neue'] text-2xl md:text-3xl text-white tracking-wide">
                  Σεζον <span className="text-[#F5A623]">2025/26</span>
                </h2>
              </div>
            </div>
            <div className="bg-gradient-to-br from-[#101010] via-[#0a0a0a] to-[#101010] border border-[#F5A623]/30 rounded-2xl overflow-hidden">
              <StatsStrip stats={stats} />
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export { isOurTeam, displayTeamName, CLUB_NAME_GOLD };
